import {useCallback, useEffect, useState} from 'react';

import {API_CONFIG} from '../contentGenerator/config';
import {
    normalizeProductDetailFields,
    resetProductDetailFieldSettings as createDefaultProductDetailFieldSettings,
} from './productDetails';
import {apiFetch} from './apiFetch';

const SETTINGS_URL = `${API_CONFIG.PROXY_URL}/product-detail-fields`;

const createDefaultSettings = () => createDefaultProductDetailFieldSettings();

export const resetProductDetailFieldSettings =
    createDefaultProductDetailFieldSettings;

const normalizeSettings = (settings) => ({
    fields: normalizeProductDetailFields(settings, {
        fallbackToDefaults: !settings,
    }),
});

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        throw new Error(
            payload?.error ||
                payload?.message ||
                (typeof payload === 'string' && payload) ||
                fallbackMessage,
        );
    }

    return payload;
}

export async function loadProductDetailFieldSettings() {
    if (typeof fetch !== 'function') return createDefaultSettings();

    try {
        const response = await apiFetch(SETTINGS_URL);
        const payload = await parseApiResponse(
            response,
            'Could not load product detail field settings.',
        );
        return payload ? normalizeSettings(payload) : createDefaultSettings();
    } catch {
        return createDefaultSettings();
    }
}

export async function saveProductDetailFieldSettings(settings) {
    if (typeof fetch !== 'function') return normalizeSettings(settings);

    const payload = normalizeSettings(settings);
    const response = await apiFetch(SETTINGS_URL, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    });
    const saved = await parseApiResponse(
        response,
        'Could not save product detail field settings.',
    );

    return normalizeSettings(saved);
}

export function useProductDetailFieldSettings() {
    const [settings, setSettings] = useState(createDefaultSettings);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;

        (async () => {
            setLoading(true);
            const loadedSettings = await loadProductDetailFieldSettings();
            if (!active) return;
            setSettings(loadedSettings);
            setError('');
            setLoading(false);
        })();

        return () => {
            active = false;
        };
    }, []);

    const saveSettings = useCallback(async (nextSettings) => {
        setSaving(true);
        try {
            const saved = await saveProductDetailFieldSettings(nextSettings);
            setSettings(saved);
            setError('');
            return saved;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    return {
        error,
        fields: settings.fields,
        loading,
        saveSettings,
        saving,
        settings,
    };
}
